import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  HeartOutlined,
  HistoryOutlined,
  HomeOutlined,
  IdcardOutlined,
  PhoneOutlined,
  PlusOutlined,
  StarFilled,
  SwapOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  studentsApi,
  type BehaviorNote,
  type BehaviorNoteType,
  type EnrollRequest,
  type EnrollmentRow,
  type Guardian,
  type StudentDetail,
  type StudentStatus,
  type UpsertBehaviorNote,
  type UpsertGuardian,
  type UpsertStudent,
} from '@/api/students';
import { academicsApi } from '@/api/academics';
import { feeApi } from '@/api/fee';
import { frontDeskApi } from '@/api/frontdesk';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { useAbility } from '@/auth/ability-provider';
import { ProfileHero } from '@/shared/components/profile-hero';
import { SummaryRail, type SummaryItem } from '@/shared/components/summary-rail';
import { MaskedAmount, RevealGroup, RevealToggle } from '@/shared/components/masked-amount';
import { AuditHistoryDrawer } from '@/shared/components/ui';
import { extractError } from '@/shared/lib/error-message';
import { fileUrl } from '@/shared/lib/file-url';
import { useStudentLookups } from './lookups';

const STATUS_LABEL: Record<StudentStatus, string> = {
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  LEFT: 'Left',
  GRADUATED: 'Graduated',
};
const STATUS_COLOR: Record<StudentStatus, string> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  LEFT: 'default',
  GRADUATED: 'processing',
};

export function StudentDetailPage() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const ability = useAbility();
  const canViewFees = ability.can('view', 'StudentFees');
  const canEditStudent = ability.can('edit', 'Student');
  const canViewGuardians = ability.can('view', 'StudentGuardian');
  const canViewHistory = useAuthStore(
    (s) => s.hasAuthority('ROLE_SUPER_ADMIN') || s.hasAuthority('ROLE_PRINCIPAL'),
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['students', 'detail', id],
    queryFn: () => studentsApi.get(id),
    enabled: Number.isFinite(id),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;

  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load student"
        description={extractError(error)}
        action={<Button onClick={() => navigate('/students')}>Back to list</Button>}
      />
    );
  }

  const fullName = `${data.firstName} ${data.lastName}`;
  const currentEnrollmentId = data.currentEnrollment?.id ?? null;
  const ageYears = data.dateOfBirth ? dayjs().diff(dayjs(data.dateOfBirth), 'year') : null;
  const initials = (data.firstName.charAt(0) + (data.lastName.charAt(0) || '')).toUpperCase();

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <ProfileHero
        eyebrow={`Module / Students · ${data.registrationNumber}${data.legacyAdmissionNumber ? ` · Old ${data.legacyAdmissionNumber}` : ''}`}
        title={fullName}
        avatar={{ src: data.photoPath, fallback: initials, shape: 'circle', size: 88 }}
        statusPill={{ label: STATUS_LABEL[data.currentStatus], tone: STATUS_COLOR[data.currentStatus] }}
        subtitle={
          <Space size={10} wrap>
            {data.currentEnrollment && (
              <Tag variant="filled" color="processing">
                {data.currentEnrollment.className} · {data.currentEnrollment.sectionName}
              </Tag>
            )}
            {ageYears != null && (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {ageYears} yrs · DOB {dayjs(data.dateOfBirth!).format('D MMM YYYY')}
              </Typography.Text>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Registered {dayjs(data.createdAt).format('D MMM YYYY')}
            </Typography.Text>
          </Space>
        }
        actions={
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/students')}>
              Back
            </Button>
            {canViewHistory && (
              <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
                History
              </Button>
            )}
            {currentEnrollmentId != null && canViewFees && (
              <Button
                icon={<DollarOutlined />}
                onClick={() => navigate(`/fee/student/${currentEnrollmentId}?action=collect`)}
              >
                Pay fee
              </Button>
            )}
            {canEditStudent && (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                Edit profile
              </Button>
            )}
          </Space>
        }
      />

      <AuditHistoryDrawer
        entityType="student"
        entityId={data.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={16}>
          <Tabs
            defaultActiveKey="profile"
            items={[
              { key: 'profile', label: 'Profile', children: <ProfileTab student={data} /> },
              ...(canViewGuardians ? [{
                key: 'guardians',
                label: `Guardians${data.guardians.length ? ` · ${data.guardians.length}` : ''}`,
                children: <GuardiansTab student={data} />,
              }] : []),
              { key: 'enrollment', label: 'Enrollment', children: <EnrollmentTab student={data} /> },
              { key: 'status', label: 'Status', children: <StatusTab student={data} /> },
              { key: 'behavior', label: 'Behaviour', children: <BehaviorTab student={data} /> },
            ]}
          />
        </Col>
        <Col xs={24} lg={8}>
          <StudentSummary student={data} />
        </Col>
      </Row>

      <Drawer
        title={
          <Space size={10}>
            <Avatar
              size={32}
              src={data.photoPath ? fileUrl(data.photoPath) ?? undefined : undefined}
              icon={!data.photoPath ? <UserOutlined /> : undefined}
            />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{fullName}</div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {data.registrationNumber}
              </Typography.Text>
            </div>
          </Space>
        }
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size={640}
        destroyOnHidden
        styles={{ body: { padding: 0 } }}
      >
        {editOpen && <EditProfileForm student={data} onDone={() => setEditOpen(false)} />}
      </Drawer>
    </div>
  );
}

/* ---------- Summary rail ---------- */

function StudentSummary({ student }: { student: StudentDetail }) {
  const enrollmentId = student.currentEnrollment?.id ?? null;
  const ability = useAbility();
  const canSeeFees = ability.can('view', 'StudentFees');

  const ledgerQuery = useQuery({
    queryKey: ['fee', 'ledger', enrollmentId],
    queryFn: () => feeApi.ledger(enrollmentId!),
    enabled: enrollmentId != null && canSeeFees,
    staleTime: 60 * 1000,
  });

  const ledger = ledgerQuery.data;
  const outstanding = ledger ? Number(ledger.outstanding) : 0;
  const billed = ledger ? Number(ledger.billed) : 0;
  const paid = ledger ? Number(ledger.paid) : 0;
  const lastReceipt = ledger?.receipts?.[0];

  const financialItems: SummaryItem[] = canSeeFees ? [
    {
      label: 'Outstanding',
      value: enrollmentId == null
        ? '—'
        : <MaskedAmount value={`Rs ${outstanding.toLocaleString('en-PK')}`} />,
      hint: enrollmentId == null
        ? 'Not enrolled'
        : (
          <>
            Billed <MaskedAmount value={`Rs ${billed.toLocaleString('en-PK')}`} maskWidth={4} />
            {' · '}
            Paid <MaskedAmount value={`Rs ${paid.toLocaleString('en-PK')}`} maskWidth={4} />
          </>
        ),
      tone: outstanding > 0 ? 'danger' : 'success',
    },
    {
      label: 'Last payment',
      value: lastReceipt
        ? <MaskedAmount value={`Rs ${Number(lastReceipt.totalAmount).toLocaleString('en-PK')}`} />
        : '—',
      hint: lastReceipt
        ? `Receipt #${lastReceipt.receiptNumber} · ${dayjs(lastReceipt.paymentDate).format('D MMM YYYY')}`
        : 'No receipts yet',
    },
  ] : [];

  const items: SummaryItem[] = [
    ...financialItems,
    {
      label: 'Status',
      value: STATUS_LABEL[student.currentStatus],
      hint: student.statusEffectiveDate
        ? `Since ${dayjs(student.statusEffectiveDate).format('D MMM YYYY')}`
        : undefined,
      tone:
        student.currentStatus === 'ACTIVE'
          ? 'success'
          : student.currentStatus === 'SUSPENDED'
            ? 'warning'
            : 'neutral',
    },
  ];

  // Wrap in a RevealGroup only if the rail actually shows financial figures —
  // a Teacher viewing the same rail sees only the Status item and needs no
  // toggle at all.
  const hasMasked = canSeeFees;
  if (!hasMasked) {
    return <SummaryRail items={items} isLoading={false} />;
  }
  return (
    <RevealGroup>
      <SummaryRail
        items={items}
        isLoading={enrollmentId != null && ledgerQuery.isLoading}
        headerExtra={<RevealToggle />}
      />
    </RevealGroup>
  );
}

/* ---------- Profile tab ---------- */

function ProfileTab({ student }: { student: StudentDetail }) {
  const lookups = useStudentLookups();
  const lookupName = (rows: { id: number; name: string }[], id: number | null) =>
    id == null ? '—' : rows.find((r) => r.id === id)?.name ?? '—';
  const cityName = student.cityId
    ? (() => {
        const c = lookups.cities.find((c) => c.id === student.cityId);
        return c ? (c.area ? `${c.name} · ${c.area}` : c.name) : '—';
      })()
    : '—';

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <ProfileSection icon={<IdcardOutlined />} title="Personal">
        <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Registration #">{student.registrationNumber}</Descriptions.Item>
          <Descriptions.Item label="Old admission #">{student.legacyAdmissionNumber ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Date of birth">
            {student.dateOfBirth ? dayjs(student.dateOfBirth).format('D MMM YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Gender">{lookupName(lookups.genders, student.genderId)}</Descriptions.Item>
          <Descriptions.Item label="Blood group">{lookupName(lookups.bloodGroups, student.bloodGroupId)}</Descriptions.Item>
          <Descriptions.Item label="Religion">{lookupName(lookups.religions, student.religionId)}</Descriptions.Item>
          <Descriptions.Item label="Nationality">{lookupName(lookups.nationalities, student.nationalityId)}</Descriptions.Item>
        </Descriptions>
      </ProfileSection>

      <ProfileSection icon={<HomeOutlined />} title="Address & emergency contact">
        <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Address" span={{ xs: 1, md: 2 }}>
            {student.addressLine || <Typography.Text type="secondary">—</Typography.Text>}
          </Descriptions.Item>
          <Descriptions.Item label="City">{cityName}</Descriptions.Item>
          <Descriptions.Item label="Emergency contact">
            {student.emergencyContactName ? (
              <Space size={6} wrap>
                <span>{student.emergencyContactName}</span>
                {student.emergencyRelation && (
                  <Typography.Text type="secondary">· {student.emergencyRelation}</Typography.Text>
                )}
                {student.emergencyContactPhone && (
                  <Tag icon={<PhoneOutlined />} variant="filled">
                    {student.emergencyContactPhone}
                  </Tag>
                )}
              </Space>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </ProfileSection>

      <ProfileSection icon={<HeartOutlined />} title="Medical & previous school">
        <Descriptions column={{ xs: 1, md: 2 }} size="small" colon={false}>
          <Descriptions.Item label="Medical notes" span={{ xs: 1, md: 2 }}>
            {student.medicalNotes || <Typography.Text type="secondary">—</Typography.Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Previous school" span={{ xs: 1, md: 2 }}>
            {student.prevSchoolName ? (
              <Space wrap>
                <span>{student.prevSchoolName}</span>
                {student.prevSchoolLeavingClass && <Tag>{student.prevSchoolLeavingClass}</Tag>}
                {student.prevSchoolTcNumber && (
                  <Typography.Text type="secondary">TC: {student.prevSchoolTcNumber}</Typography.Text>
                )}
              </Space>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </ProfileSection>
    </Space>
  );
}

function ProfileSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      styles={{ body: { padding: '14px 18px 16px' } }}
      title={
        <Space size={8} style={{ fontSize: 14 }}>
          <span style={{ color: 'var(--ant-color-primary)', fontSize: 16 }}>{icon}</span>
          <span>{title}</span>
        </Space>
      }
    >
      {children}
    </Card>
  );
}

/* ---------- Edit profile drawer form ---------- */

interface EditProfileShape {
  firstName: string;
  lastName: string;
  dateOfBirth?: Dayjs;
  genderId?: number;
  bloodGroupId?: number;
  religionId?: number;
  nationalityId?: number;
  addressLine?: string;
  cityId?: number;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyRelation?: string;
  medicalNotes?: string;
  prevSchoolName?: string;
  prevSchoolLeavingClass?: string;
  prevSchoolTcNumber?: string;
  legacyAdmissionNumber?: string;
}

function EditProfileForm({ student, onDone }: { student: StudentDetail; onDone: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const lookups = useStudentLookups();
  const [form] = Form.useForm<EditProfileShape>();

  useEffect(() => {
    form.setFieldsValue({
      firstName: student.firstName,
      lastName: student.lastName,
      dateOfBirth: student.dateOfBirth ? dayjs(student.dateOfBirth) : undefined,
      genderId: student.genderId ?? undefined,
      bloodGroupId: student.bloodGroupId ?? undefined,
      religionId: student.religionId ?? undefined,
      nationalityId: student.nationalityId ?? undefined,
      addressLine: student.addressLine ?? undefined,
      cityId: student.cityId ?? undefined,
      emergencyContactName: student.emergencyContactName ?? undefined,
      emergencyContactPhone: student.emergencyContactPhone ?? undefined,
      emergencyRelation: student.emergencyRelation ?? undefined,
      medicalNotes: student.medicalNotes ?? undefined,
      prevSchoolName: student.prevSchoolName ?? undefined,
      prevSchoolLeavingClass: student.prevSchoolLeavingClass ?? undefined,
      prevSchoolTcNumber: student.prevSchoolTcNumber ?? undefined,
      legacyAdmissionNumber: student.legacyAdmissionNumber ?? undefined,
    });
  }, [form, student]);

  const update = useMutation({
    mutationFn: (body: UpsertStudent) => studentsApi.update(student.id, body),
    onSuccess: () => {
      message.success('Profile updated');
      qc.invalidateQueries({ queryKey: ['students', 'detail', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'search'] });
      onDone();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const onFinish = (values: EditProfileShape) => {
    update.mutate({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : null,
      genderId: values.genderId ?? null,
      bloodGroupId: values.bloodGroupId ?? null,
      religionId: values.religionId ?? null,
      nationalityId: values.nationalityId ?? null,
      addressLine: values.addressLine?.trim() || null,
      cityId: values.cityId ?? null,
      emergencyContactName: values.emergencyContactName?.trim() || null,
      emergencyContactPhone: values.emergencyContactPhone?.trim() || null,
      emergencyRelation: values.emergencyRelation?.trim() || null,
      medicalNotes: values.medicalNotes?.trim() || null,
      prevSchoolName: values.prevSchoolName?.trim() || null,
      prevSchoolLeavingClass: values.prevSchoolLeavingClass?.trim() || null,
      prevSchoolTcNumber: values.prevSchoolTcNumber?.trim() || null,
      legacyAdmissionNumber: values.legacyAdmissionNumber?.trim() || null,
    });
  };

  const opt = (rows: { id: number; name: string }[]) =>
    rows.map((r) => ({ value: r.id, label: r.name }));
  const cityOpt = (rows: { id: number; name: string; area: string | null }[]) =>
    rows.map((r) => ({ value: r.id, label: r.area ? `${r.name} · ${r.area}` : r.name }));

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} disabled={update.isPending || lookups.isLoading}>
      <div style={{ padding: '18px 20px 96px', display: 'grid', gap: 14 }}>
        <EditSection icon={<IdcardOutlined />} title="Personal">
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="First name" name="firstName" rules={[{ required: true, max: 100 }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Last name" name="lastName" rules={[{ required: true, max: 100 }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Date of birth" name="dateOfBirth">
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d && d.isAfter(dayjs())} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12}>
              <Form.Item label="Gender" name="genderId">
                <Select allowClear options={opt(lookups.genders)} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item label="Blood group" name="bloodGroupId">
                <Select allowClear options={opt(lookups.bloodGroups)} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item label="Religion" name="religionId">
                <Select allowClear options={opt(lookups.religions)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Nationality" name="nationalityId">
                <Select allowClear options={opt(lookups.nationalities)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Old admission # (legacy)"
                name="legacyAdmissionNumber"
                tooltip="Pre-system admission number (e.g. TSS-001). Searchable from the students list."
              >
                <Input maxLength={20} placeholder="e.g. TSS-001" />
              </Form.Item>
            </Col>
          </Row>
        </EditSection>

        <EditSection icon={<HomeOutlined />} title="Address &amp; emergency contact">
          <Row gutter={[12, 0]}>
            <Col span={24}>
              <Form.Item label="Address" name="addressLine">
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="House #, Street, Locality" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="City" name="cityId">
                <Select allowClear showSearch optionFilterProp="label" options={cityOpt(lookups.cities)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Emergency name" name="emergencyContactName">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item label="Emergency phone" name="emergencyContactPhone">
                <Input prefix={<PhoneOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />} placeholder="03xx-xxxxxxx" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item label="Relation" name="emergencyRelation">
                <Input placeholder="e.g. Uncle" />
              </Form.Item>
            </Col>
          </Row>
        </EditSection>

        <EditSection icon={<HeartOutlined />} title="Medical &amp; previous school">
          <Row gutter={[12, 0]}>
            <Col span={24}>
              <Form.Item label="Medical notes" name="medicalNotes">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Allergies, conditions, recurring medication" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Previous school" name="prevSchoolName">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Leaving class" name="prevSchoolLeavingClass">
                <Input placeholder="e.g. Grade 4" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="TC number" name="prevSchoolTcNumber">
                <Input placeholder="Transfer cert. number" />
              </Form.Item>
            </Col>
          </Row>
        </EditSection>
      </div>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--ant-color-bg-container)',
          borderTop: '1px solid var(--ant-color-border-secondary)',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
        }}
      >
        <Button onClick={onDone} disabled={update.isPending}>Cancel</Button>
        <Button type="primary" htmlType="submit" loading={update.isPending} icon={<EditOutlined />}>
          Save changes
        </Button>
      </div>
    </Form>
  );
}

function EditSection({ icon, title, children }: { icon: React.ReactNode; title: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card
      variant="outlined"
      styles={{
        header: { minHeight: 40, padding: '0 16px' },
        body: { padding: '14px 16px 2px' },
      }}
      title={
        <Space size={8} style={{ fontSize: 13 }}>
          <span style={{ color: 'var(--ant-color-primary)', fontSize: 15, display: 'inline-flex' }}>{icon}</span>
          <Typography.Text strong style={{ fontSize: 13 }}>{title}</Typography.Text>
        </Space>
      }
    >
      {children}
    </Card>
  );
}

/* ---------- Guardians tab ---------- */

function GuardiansTab({ student }: { student: StudentDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:DELETE'));
  const lookups = useStudentLookups();
  const [editing, setEditing] = useState<Guardian | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<UpsertGuardian>();

  const invalidate = () => qc.invalidateQueries({ queryKey: ['students', 'detail', student.id] });

  const upsert = useMutation({
    mutationFn: (body: UpsertGuardian) =>
      editing
        ? studentsApi.updateGuardian(student.id, editing.id, body)
        : studentsApi.addGuardian(student.id, body),
    onSuccess: () => {
      message.success(editing ? 'Guardian updated' : 'Guardian added');
      invalidate();
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (gid: number) => studentsApi.deleteGuardian(student.id, gid),
    onSuccess: () => {
      message.success('Guardian removed');
      invalidate();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ primaryContact: student.guardians.length === 0 } as UpsertGuardian);
  };
  const openEdit = (g: Guardian) => {
    setCreating(false);
    setEditing(g);
    form.setFieldsValue({
      relationshipTypeId: g.relationshipTypeId,
      fullName: g.fullName,
      cnic: g.cnic ?? '',
      phone: g.phone ?? '',
      occupation: g.occupation ?? '',
      primaryContact: g.primaryContact,
    });
  };

  const open = creating || editing != null;

  return (
    <Card
      title="Guardians"
      extra={
        canEdit ? (
          <Button icon={<UserAddOutlined />} onClick={openCreate}>
            Add guardian
          </Button>
        ) : null
      }
    >
      {student.guardians.length === 0 ? (
        <Empty description="No guardians on file" />
      ) : (
        <Row gutter={[16, 16]}>
          {student.guardians.map((g) => (
            <Col key={g.id} xs={24} md={12}>
              <GuardianCard
                guardian={g}
                relationLabel={lookups.relations.find((r) => r.id === g.relationshipTypeId)?.name ?? '—'}
                onEdit={canEdit ? () => openEdit(g) : undefined}
                onRemove={canDelete ? () => remove.mutate(g.id) : undefined}
                removing={remove.isPending}
              />
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editing ? `Edit ${editing.fullName}` : 'Add guardian'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values: UpsertGuardian) =>
              upsert.mutate({
                relationshipTypeId: values.relationshipTypeId,
                fullName: values.fullName.trim(),
                cnic: values.cnic?.toString().trim() || undefined,
                phone: values.phone?.toString().trim() || undefined,
                occupation: values.occupation?.toString().trim() || undefined,
                primaryContact: values.primaryContact,
              })
            }
          >
            <Form.Item
              label="Full name"
              name="fullName"
              rules={[{ required: true, max: 200 }]}
            >
              <Input autoFocus />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Relationship"
                  name="relationshipTypeId"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={lookups.relations.map((r) => ({ value: r.id, label: r.name }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Phone" name="phone" rules={[{ max: 20 }]}>
                  <Input placeholder="03xx-xxxxxxx" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="CNIC" name="cnic" rules={[{ max: 15 }]}>
                  <Input placeholder="35202-xxxxxxx-x" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Occupation" name="occupation" rules={[{ max: 100 }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label="Primary contact"
              name="primaryContact"
              valuePropName="checked"
              tooltip="Used as the default phone & name on the student list."
            >
              <Switch />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}

function GuardianCard({
  guardian,
  relationLabel,
  onEdit,
  onRemove,
  removing,
}: {
  guardian: Guardian;
  relationLabel: string;
  onEdit?: () => void;
  onRemove?: () => void;
  removing?: boolean;
}) {
  return (
    <Card
      hoverable
      styles={{ body: { padding: 16 } }}
      style={{ height: '100%' }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Avatar size={48} icon={<UserOutlined />} style={{ background: 'var(--ant-color-fill-secondary)' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography.Text strong style={{ fontSize: 15 }}>{guardian.fullName}</Typography.Text>
            {guardian.primaryContact && (
              <Tag color="processing" icon={<StarFilled />} variant="filled">
                Primary
              </Tag>
            )}
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{relationLabel}</Typography.Text>

          <Space orientation="vertical" size={4} style={{ marginTop: 10 }}>
            {guardian.phone && (
              <Space size={6}>
                <PhoneOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />
                <Typography.Text style={{ fontSize: 13 }}>{guardian.phone}</Typography.Text>
              </Space>
            )}
            {guardian.cnic && (
              <Space size={6}>
                <IdcardOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />
                <Typography.Text style={{ fontSize: 13 }}>{guardian.cnic}</Typography.Text>
              </Space>
            )}
            {guardian.occupation && (
              <Space size={6}>
                <BankOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />
                <Typography.Text style={{ fontSize: 13 }}>{guardian.occupation}</Typography.Text>
              </Space>
            )}
          </Space>
        </div>
        {(onEdit || onRemove) && (
          <Space size={4}>
            {onEdit && (
              <Tooltip title="Edit">
                <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
              </Tooltip>
            )}
            {onRemove && (
              <Popconfirm
                title="Remove guardian?"
                okText="Remove"
                okButtonProps={{ danger: true }}
                onConfirm={onRemove}
              >
                <Button size="small" danger type="text" icon={<DeleteOutlined />} loading={removing} />
              </Popconfirm>
            )}
          </Space>
        )}
      </div>
    </Card>
  );
}

/* ---------- Enrollment tab ---------- */

function EnrollmentTab({ student }: { student: StudentDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:EDIT'));
  const lookups = useStudentLookups();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [form] = Form.useForm<EnrollFormShape>();
  const [transferForm] = Form.useForm<TransferFormShape>();

  const current = student.currentEnrollment;

  const { data: history, isLoading } = useQuery({
    queryKey: ['students', 'enrollments', student.id],
    queryFn: () => studentsApi.history(student.id),
  });

  const transfersQuery = useQuery({
    queryKey: ['students', 'transfers', student.id],
    queryFn: () => academicsApi.promotionHistory(student.id),
    select: (rows) => rows.filter((r) => r.decision === 'TRANSFER'),
  });
  const transfers = transfersQuery.data ?? [];

  const [yearId, setYearId] = useState<number | null>(null);
  useEffect(() => {
    if (yearId == null && lookups.years.length > 0) {
      const current = lookups.years.find((y) => y.current) ?? lookups.years[0];
      if (current) setYearId(current.id);
    }
  }, [yearId, lookups.years]);

  const csQuery = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });
  const classSections = csQuery.data ?? [];

  const transferCsQuery = useQuery({
    queryKey: ['setup', 'class-sections', current?.academicYearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(current?.academicYearId ?? undefined),
    enabled: transferOpen && current != null,
    staleTime: 5 * 60 * 1000,
  });
  const transferSections = transferCsQuery.data ?? [];

  const enroll = useMutation({
    mutationFn: (body: EnrollRequest) => studentsApi.enroll(student.id, body),
    onSuccess: () => {
      message.success('Enrolled');
      qc.invalidateQueries({ queryKey: ['students', 'enrollments', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'detail', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'search'] });
      setEnrollOpen(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const transfer = useMutation({
    mutationFn: (body: { classSectionId: number; reason?: string }) =>
      studentsApi.transferSection(student.id, body),
    onSuccess: () => {
      message.success('Transferred');
      qc.invalidateQueries({ queryKey: ['students', 'enrollments', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'transfers', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'detail', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'search'] });
      setTransferOpen(false);
      transferForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const onFinish = (values: EnrollFormShape) => {
    enroll.mutate({
      academicYearId: values.academicYearId,
      classSectionId: values.classSectionId,
      enrollmentDate: values.enrollmentDate.format('YYYY-MM-DD'),
    });
  };

  const sortedHistory = (history ?? []).slice().sort((a, b) =>
    dayjs(b.enrollmentDate).valueOf() - dayjs(a.enrollmentDate).valueOf(),
  );

  // One timeline mixing enrollment rows and mid-year transfers, newest first.
  const timelineItems = [
    ...sortedHistory.map((row) => ({
      sortKey: dayjs(row.enrollmentDate).valueOf(),
      color: row.active ? 'green' : 'gray',
      label: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(row.enrollmentDate).format('D MMM YYYY')}
        </Typography.Text>
      ),
      children: (
        <div>
          <Space size={8}>
            <Typography.Text strong>{row.academicYearName}</Typography.Text>
            {row.active && <Tag color="success" variant="filled">Current</Tag>}
          </Space>
          <div>
            <Typography.Text>{row.className} · {row.sectionName}</Typography.Text>
          </div>
        </div>
      ),
    })),
    ...transfers.map((t) => ({
      sortKey: dayjs(t.promotedAt).valueOf(),
      color: 'blue',
      label: (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(t.promotedAt).format('D MMM YYYY')}
        </Typography.Text>
      ),
      children: (
        <div>
          <Space size={8}>
            <Typography.Text strong>Section transfer</Typography.Text>
            <Tag color="processing" variant="filled">Transfer</Tag>
          </Space>
          <div>
            <Typography.Text>
              {t.fromClassSectionName ?? '—'} → {t.toClassSectionName ?? '—'}
            </Typography.Text>
          </div>
          {t.reason && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t.reason}
            </Typography.Text>
          )}
        </div>
      ),
    })),
  ].sort((a, b) => b.sortKey - a.sortKey);

  return (
    <Card
      title="Enrollment"
      extra={
        current && current.active ? (
          canEdit ? (
            <Button icon={<SwapOutlined />} onClick={() => {
              transferForm.resetFields();
              setTransferOpen(true);
            }}>
              Transfer section
            </Button>
          ) : null
        ) : canCreate ? (
          <Button icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            const currentYear = lookups.years.find((y) => y.current);
            form.setFieldsValue({
              academicYearId: currentYear?.id,
              enrollmentDate: dayjs(),
            } as EnrollFormShape);
            setEnrollOpen(true);
          }}>
            Enroll for a year
          </Button>
        ) : null
      }
    >
      {student.currentEnrollment ? (
        <Alert
          type="success"
          showIcon
          message={`Currently enrolled in ${student.currentEnrollment.className} · ${student.currentEnrollment.sectionName}`}
          description={`Year ${student.currentEnrollment.academicYearName} · enrolled ${dayjs(student.currentEnrollment.enrollmentDate).format('D MMM YYYY')}`}
          style={{ marginBottom: 20 }}
        />
      ) : (
        <Alert type="info" showIcon message="Not enrolled in any class for the current year." style={{ marginBottom: 20 }} />
      )}

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : timelineItems.length === 0 ? (
        <Empty description="No enrollment history" />
      ) : (
        <Timeline mode="left" items={timelineItems} />
      )}

      <Modal
        title="Enroll in a class · section"
        open={enrollOpen}
        onCancel={() => setEnrollOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={enroll.isPending}
        okText="Enroll"
        destroyOnHidden
      >
        {enrollOpen && (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={(changed) => {
              if (changed.academicYearId != null) {
                setYearId(changed.academicYearId);
                form.setFieldValue('classSectionId', undefined);
              }
            }}
          >
            <Form.Item
              label="Academic year"
              name="academicYearId"
              rules={[{ required: true }]}
            >
              <Select
                options={lookups.years.map((y) => ({
                  value: y.id,
                  label: y.current ? `${y.name} · current` : y.name,
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Class · Section"
              name="classSectionId"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={yearId == null ? 'Pick a year first' : 'Choose…'}
                options={classSections
                  .slice()
                  .sort(
                    (a, b) =>
                      a.classSortOrder - b.classSortOrder ||
                      a.sectionName.localeCompare(b.sectionName),
                  )
                  .map((c) => ({
                    value: c.id,
                    label: `${c.className} · ${c.sectionName}`,
                  }))}
              />
            </Form.Item>
            <Form.Item
              label="Enrollment date"
              name="enrollmentDate"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Transfer to another section"
        open={transferOpen}
        onCancel={() => setTransferOpen(false)}
        onOk={() => transferForm.submit()}
        confirmLoading={transfer.isPending}
        okText="Transfer"
        destroyOnHidden
      >
        {transferOpen && current && (
          <Form
            form={transferForm}
            layout="vertical"
            onFinish={(values: TransferFormShape) =>
              transfer.mutate({
                classSectionId: values.classSectionId,
                reason: values.reason?.trim() || undefined,
              })
            }
          >
            <Alert
              type="info"
              showIcon
              message={`Currently in ${current.className} · ${current.sectionName} (${current.academicYearName})`}
              style={{ marginBottom: 16 }}
            />
            <Form.Item
              label="New class · section"
              name="classSectionId"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={transferCsQuery.isLoading}
                placeholder="Choose…"
                options={transferSections
                  .filter((c) => c.id !== current.classSectionId)
                  .slice()
                  .sort(
                    (a, b) =>
                      a.classSortOrder - b.classSortOrder ||
                      a.sectionName.localeCompare(b.sectionName),
                  )
                  .map((c) => ({
                    value: c.id,
                    label: `${c.className} · ${c.sectionName}`,
                  }))}
              />
            </Form.Item>
            <Form.Item label="Reason (optional)" name="reason">
              <Input.TextArea rows={2} maxLength={500} placeholder="e.g. parent request, section balancing" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}

interface EnrollFormShape {
  academicYearId: number;
  classSectionId: number;
  enrollmentDate: Dayjs;
}

interface TransferFormShape {
  classSectionId: number;
  reason?: string;
}

/* ---------- Status tab ---------- */

interface StatusLogRow {
  id: number;
  fromStatus: StudentStatus | null;
  toStatus: StudentStatus;
  effectiveDate: string;
  reason: string | null;
  changedAt: string;
}

function StatusTab({ student }: { student: StudentDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:EDIT'));
  const canIssueDocs = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const lookups = useStudentLookups();
  const [open, setOpen] = useState(false);
  const [issueTcOpen, setIssueTcOpen] = useState(false);
  const [tcEffectiveDate, setTcEffectiveDate] = useState<string | null>(null);
  const [form] = Form.useForm<StatusFormShape>();

  const { data: log, isLoading } = useQuery({
    queryKey: ['students', 'status-history', student.id],
    queryFn: () => studentsApi.statusHistory(student.id) as Promise<StatusLogRow[]>,
  });

  const change = useMutation({
    mutationFn: (body: { toStatus: StudentStatus; effectiveDate: string; reason?: string; leavingReasonId?: number }) =>
      studentsApi.changeStatus(student.id, body),
    onSuccess: (_data, vars) => {
      message.success('Status changed');
      qc.invalidateQueries({ queryKey: ['students', 'status-history', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'detail', student.id] });
      qc.invalidateQueries({ queryKey: ['students', 'search'] });
      setOpen(false);
      form.resetFields();
      // Per requirements: TC issuance is logged via Front Desk when a student is marked LEFT.
      if (vars.toStatus === 'LEFT' && canIssueDocs) {
        setTcEffectiveDate(vars.effectiveDate);
        setIssueTcOpen(true);
      }
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedStatus = Form.useWatch('toStatus', form);
  const showLeavingReason = watchedStatus === 'LEFT' || watchedStatus === 'GRADUATED';

  const columns: TableProps<StatusLogRow>['columns'] = [
    {
      title: 'When',
      dataIndex: 'changedAt',
      width: 170,
      render: (d: string) => dayjs(d).format('D MMM YYYY HH:mm'),
    },
    {
      title: 'Transition',
      render: (_, row) => (
        <Space>
          {row.fromStatus ? (
            <Tag color={STATUS_COLOR[row.fromStatus]}>{STATUS_LABEL[row.fromStatus]}</Tag>
          ) : (
            <Tag>—</Tag>
          )}
          <span style={{ opacity: 0.6 }}>→</span>
          <Tag color={STATUS_COLOR[row.toStatus]}>{STATUS_LABEL[row.toStatus]}</Tag>
        </Space>
      ),
    },
    {
      title: 'Effective',
      dataIndex: 'effectiveDate',
      width: 140,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    { title: 'Reason', dataIndex: 'reason', render: (r: string | null) => r || '—' },
  ];

  return (
    <Card
      title="Status"
      extra={
        canEdit ? (
          <Button icon={<EditOutlined />} onClick={() => {
            form.resetFields();
            form.setFieldsValue({
              effectiveDate: dayjs(),
              toStatus: 'ACTIVE',
            } as StatusFormShape);
            setOpen(true);
          }}>
            Change status
          </Button>
        ) : null
      }
    >
      <Alert
        type="info"
        showIcon
        message={
          <Space>
            <span>Current status:</span>
            <Tag color={STATUS_COLOR[student.currentStatus]}>{STATUS_LABEL[student.currentStatus]}</Tag>
            {student.statusEffectiveDate && (
              <Typography.Text type="secondary">
                since {dayjs(student.statusEffectiveDate).format('D MMM YYYY')}
              </Typography.Text>
            )}
          </Space>
        }
        style={{ marginBottom: 16 }}
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={log ?? []}
        pagination={false}
        locale={{ emptyText: <Empty description="No status changes recorded" /> }}
      />

      <Modal
        title="Change status"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={change.isPending}
        okText="Apply"
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values: StatusFormShape) =>
              change.mutate({
                toStatus: values.toStatus,
                effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
                reason: values.reason?.trim() || undefined,
                leavingReasonId: values.leavingReasonId ?? undefined,
              })
            }
          >
            <Form.Item label="New status" name="toStatus" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'ACTIVE', label: STATUS_LABEL.ACTIVE },
                  { value: 'SUSPENDED', label: STATUS_LABEL.SUSPENDED },
                  { value: 'LEFT', label: STATUS_LABEL.LEFT },
                  { value: 'GRADUATED', label: STATUS_LABEL.GRADUATED },
                ]}
              />
            </Form.Item>
            <Form.Item label="Effective date" name="effectiveDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            {showLeavingReason && (
              <Form.Item label="Leaving reason" name="leavingReasonId">
                <Select
                  allowClear
                  options={lookups.leavingReasons.map((r) => ({ value: r.id, label: r.name }))}
                />
              </Form.Item>
            )}
            <Form.Item label="Notes" name="reason">
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {issueTcOpen && tcEffectiveDate && (
        <IssueTcModal
          studentId={student.id}
          studentName={`${student.firstName} ${student.lastName}`}
          defaultIssueDate={tcEffectiveDate}
          onClose={() => setIssueTcOpen(false)}
        />
      )}
    </Card>
  );
}

function IssueTcModal({
  studentId,
  studentName,
  defaultIssueDate,
  onClose,
}: {
  studentId: number;
  studentName: string;
  defaultIssueDate: string;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [docTypeId, setDocTypeId] = useState<number | undefined>(undefined);
  const [issueDate, setIssueDate] = useState<Dayjs>(dayjs(defaultIssueDate));

  const { data: docTypes, isLoading } = useQuery({
    queryKey: ['frontdesk', 'document-types', 'active'],
    queryFn: () => frontDeskApi.listDocumentTypes(true),
  });

  // Default to the "TC" code if present.
  useEffect(() => {
    if (docTypeId == null && docTypes && docTypes.length > 0) {
      const tc = docTypes.find((t) => t.code.toUpperCase() === 'TC') ?? docTypes[0];
      setDocTypeId(tc.id);
    }
  }, [docTypes, docTypeId]);

  const issue = useMutation({
    mutationFn: () =>
      frontDeskApi.issueDocument({
        documentTypeId: docTypeId!,
        studentId,
        issueDate: issueDate.format('YYYY-MM-DD'),
      }),
    onSuccess: (issuance) => {
      message.success(
        `Issued · ${issuance.documentTypeName ?? 'document'} ${issuance.documentRef ?? `#${issuance.documentNumber}`}`,
      );
      onClose();
    },
    onError: (e) => message.error(extractError(e)),
  });

  return (
    <Modal
      open
      title={`Issue document for ${studentName}`}
      onCancel={onClose}
      cancelText="Skip"
      okText="Issue"
      okButtonProps={{ disabled: docTypeId == null, loading: issue.isPending }}
      onOk={() => issue.mutate()}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Marked as left — the school typically issues a Transfer Certificate at this point. You can issue it now or
        skip and do it later from Front Desk.
      </Typography.Paragraph>
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text strong>Document type</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            loading={isLoading}
            value={docTypeId}
            onChange={(v) => setDocTypeId(v)}
            options={(docTypes ?? []).map((t) => ({
              value: t.id,
              label: `${t.name}${t.code ? ` (${t.code})` : ''}`,
            }))}
          />
        </div>
        <div>
          <Typography.Text strong>Issue date</Typography.Text>
          <DatePicker
            style={{ width: '100%', marginTop: 6 }}
            value={issueDate}
            onChange={(d) => d && setIssueDate(d)}
            format="DD MMM YYYY"
            allowClear={false}
          />
        </div>
      </Space>
    </Modal>
  );
}

interface StatusFormShape {
  toStatus: StudentStatus;
  effectiveDate: Dayjs;
  reason?: string;
  leavingReasonId?: number;
}

const NOTE_TYPE_META: Record<BehaviorNoteType, { label: string; color: string; icon: string }> = {
  POSITIVE:  { label: 'Positive', color: 'success',   icon: '★' },
  CONCERN:   { label: 'Concern',  color: 'warning',   icon: '!' },
  INCIDENT:  { label: 'Incident', color: 'error',     icon: '⚠' },
};

function BehaviorTab({ student }: { student: StudentDetail }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const ability = useAbility();
  const canCreate = ability.can('create', 'Student') || ability.can('edit', 'Student');
  const canDelete = ability.can('delete', 'Student');

  const notesQuery = useQuery({
    queryKey: ['students', 'behavior-notes', student.id],
    queryFn: () => studentsApi.listBehaviorNotes(student.id),
  });

  const [openForm, setOpenForm] = useState<null | { mode: 'new' } | { mode: 'edit'; note: BehaviorNote }>(null);
  const [form] = Form.useForm<{
    noteType: BehaviorNoteType;
    title: string;
    body: string;
    occurredOn: Dayjs;
  }>();

  const createMut = useMutation({
    mutationFn: (body: UpsertBehaviorNote) => studentsApi.createBehaviorNote(student.id, body),
    onSuccess: () => {
      message.success('Note added');
      setOpenForm(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['students', 'behavior-notes', student.id] });
    },
    onError: (e) => message.error(extractError(e, 'Could not save note')),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpsertBehaviorNote }) =>
      studentsApi.updateBehaviorNote(id, body),
    onSuccess: () => {
      message.success('Note updated');
      setOpenForm(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['students', 'behavior-notes', student.id] });
    },
    onError: (e) => message.error(extractError(e, 'Could not update note')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => studentsApi.deleteBehaviorNote(id),
    onSuccess: () => {
      message.success('Note deleted');
      qc.invalidateQueries({ queryKey: ['students', 'behavior-notes', student.id] });
    },
    onError: (e) => message.error(extractError(e, 'Could not delete note')),
  });

  const openNew = () => {
    form.resetFields();
    form.setFieldsValue({ noteType: 'POSITIVE', occurredOn: dayjs() });
    setOpenForm({ mode: 'new' });
  };

  const openEdit = (note: BehaviorNote) => {
    form.setFieldsValue({
      noteType: note.noteType,
      title: note.title,
      body: note.body,
      occurredOn: dayjs(note.occurredOn),
    });
    setOpenForm({ mode: 'edit', note });
  };

  const onFinish = async () => {
    const values = await form.validateFields();
    const body: UpsertBehaviorNote = {
      noteType: values.noteType,
      title: values.title.trim(),
      body: values.body.trim(),
      occurredOn: values.occurredOn.format('YYYY-MM-DD'),
    };
    if (openForm?.mode === 'edit') {
      updateMut.mutate({ id: openForm.note.id, body });
    } else {
      createMut.mutate(body);
    }
  };

  const notes = notesQuery.data ?? [];
  const counts = notes.reduce<Record<BehaviorNoteType, number>>(
    (acc, n) => { acc[n.noteType]++; return acc; },
    { POSITIVE: 0, CONCERN: 0, INCIDENT: 0 },
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12} style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space size={8} wrap>
          <Tag color="success">★ Positive · {counts.POSITIVE}</Tag>
          <Tag color="warning">! Concern · {counts.CONCERN}</Tag>
          <Tag color="error">⚠ Incident · {counts.INCIDENT}</Tag>
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            Add note
          </Button>
        )}
      </Space>

      {notesQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : notes.length === 0 ? (
        <Empty description="No behavioural notes filed for this student yet." />
      ) : (
        <Timeline
          items={notes.map((n) => {
            const meta = NOTE_TYPE_META[n.noteType];
            return {
              color: meta.color === 'success' ? 'green'
                : meta.color === 'warning' ? 'orange'
                : meta.color === 'error' ? 'red' : undefined,
              children: (
                <div>
                  <Space wrap size={8} style={{ marginBottom: 6 }}>
                    <Tag color={meta.color}>{meta.icon} {meta.label}</Tag>
                    <Typography.Text strong>{n.title}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(n.occurredOn).format('D MMM YYYY')}
                    </Typography.Text>
                  </Space>
                  <Typography.Paragraph style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>
                    {n.body}
                  </Typography.Paragraph>
                  <Space size={10}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      by {n.createdByUsername ?? (n.createdBy != null ? `user #${n.createdBy}` : 'system')}
                      {' · '}{dayjs(n.createdAt).format('D MMM YYYY · HH:mm')}
                    </Typography.Text>
                    {canCreate && (
                      <Button type="link" size="small" onClick={() => openEdit(n)}>Edit</Button>
                    )}
                    {canDelete && (
                      <Popconfirm
                        title="Delete this note?"
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteMut.mutate(n.id)}
                      >
                        <Button type="link" size="small" danger>Delete</Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              ),
            };
          })}
        />
      )}

      <Modal
        title={openForm?.mode === 'edit' ? 'Edit note' : 'Add behavioural note'}
        open={openForm != null}
        onCancel={() => { setOpenForm(null); form.resetFields(); }}
        onOk={onFinish}
        confirmLoading={createMut.isPending || updateMut.isPending}
        okText={openForm?.mode === 'edit' ? 'Save' : 'Add'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="noteType" label="Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'POSITIVE', label: '★ Positive' },
                { value: 'CONCERN',  label: '! Concern' },
                { value: 'INCIDENT', label: '⚠ Incident' },
              ]}
            />
          </Form.Item>
          <Form.Item name="occurredOn" label="Occurred on" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="D MMM YYYY" />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true, max: 200 }]}>
            <Input placeholder="Short summary" />
          </Form.Item>
          <Form.Item name="body" label="Details" rules={[{ required: true }]}>
            <Input.TextArea rows={5} placeholder="What happened, who was involved, action taken …" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
